import type { ApiErrorBody, AuthTokens, SessionUser } from './contracts';
import { changesData, createChangeSignal } from './freshness';
import { tenantFromHost } from './tenant-host';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

const STORAGE = {
  access: 'erb.accessToken',
  refresh: 'erb.refreshToken',
  company: 'erb.company',
  language: 'erb.language',
} as const;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(Array.isArray(body.message) ? body.message.join(', ') : body.message);
    this.name = 'ApiError';
  }

  /** Stable i18n key so Arabic error text does not depend on the server locale. */
  get code(): string | undefined {
    return this.body.code;
  }
}

export const tokenStore = {
  get access(): string | null {
    return localStorage.getItem(STORAGE.access);
  },
  get refresh(): string | null {
    return localStorage.getItem(STORAGE.refresh);
  },
  set(tokens: AuthTokens): void {
    localStorage.setItem(STORAGE.access, tokens.accessToken);
    localStorage.setItem(STORAGE.refresh, tokens.refreshToken);
  },
  clear(): void {
    localStorage.removeItem(STORAGE.access);
    localStorage.removeItem(STORAGE.refresh);
  },
};

export const companyStore = {
  get(): string | null {
    return localStorage.getItem(STORAGE.company);
  },
  set(companyId: string | null): void {
    if (companyId) localStorage.setItem(STORAGE.company, companyId);
    else localStorage.removeItem(STORAGE.company);
  },
};

/**
 * The workspace named by the address — with `VITE_TENANT_BASE_HOST` set to
 * `diwan.the-alpha.co`, `butchers.diwan.the-alpha.co` signs into `butchers`.
 * Deriving it from the hostname means a bookmark cannot land a branch manager in
 * another customer's workspace, which typing a code always could.
 *
 * The base host itself is the front door and resolves to nothing, so
 * `diwan.the-alpha.co` still asks for a code. Unset in development, where one
 * machine signs into every workspace in turn.
 */
const hostTenant = tenantFromHost(
  window.location.hostname,
  ((import.meta.env.VITE_TENANT_BASE_HOST as string | undefined) ?? '')
    .split(',')
    .map((host) => host.trim())
    .filter(Boolean),
);

// A link from the control plane carries the workspace: /?tenant=<slug>. Ignored
// once the hostname has answered, so a stray query string cannot redirect a
// customer's own subdomain at somebody else's data.
const tenantFromUrl = new URLSearchParams(window.location.search).get('tenant');
if (!hostTenant && tenantFromUrl) {
  localStorage.setItem('erb.tenant', tenantFromUrl.trim().toLowerCase());
}

/**
 * The workspace (tenant) this browser signs into, sent on every request so the
 * API opens the right customer database.
 *
 * The hostname wins where it names one. Otherwise it is whatever was typed on
 * the sign-in form, then the build's default for a single-workspace install.
 * There is deliberately no hardcoded fallback: guessing a workspace is how a
 * request ends up writing to the wrong customer.
 */
export const tenantStore = {
  get(): string {
    if (hostTenant) return hostTenant;
    return (
      localStorage.getItem('erb.tenant') ??
      (import.meta.env.VITE_DEFAULT_TENANT as string | undefined) ??
      ''
    );
  },
  set(slug: string): void {
    // Nothing to remember when the address already decides, and storing it
    // would leave a stale slug behind if the host later changed.
    if (hostTenant) return;
    localStorage.setItem('erb.tenant', slug.trim().toLowerCase());
  },
  /** Whether the address named the workspace, so the form need not ask. */
  get fromHost(): boolean {
    return hostTenant !== null;
  },
};

export const languageStore = {
  get(): string {
    return localStorage.getItem(STORAGE.language) ?? 'en';
  },
  set(language: string): void {
    localStorage.setItem(STORAGE.language, language);
  },
};

/**
 * The service worker keeps a stale-while-revalidate copy of reference data
 * (vite.config.ts). Keyed by URL alone, so it has to go whenever the person or
 * workspace changes, or the next one is served the last one's copy first.
 */
export function clearReferenceCache(): void {
  if (typeof caches === 'undefined') return;
  void caches.delete('erb-reference').catch(() => undefined);
}

/**
 * Told when the session is over for good — the refresh token was refused —
 * so the app can leave for the sign-in page instead of failing every request
 * with tokens that will never work again.
 */
const expiredListeners = new Set<() => void>();
export function onSessionExpired(listener: () => void): () => void {
  expiredListeners.add(listener);
  return () => {
    expiredListeners.delete(listener);
  };
}

/**
 * Told when the server refuses a request because the account must change its
 * password first — set by HR mid-session, say. The app shows the change
 * screen instead of letting every screen fail on the same 403.
 */
const passwordChangeListeners = new Set<() => void>();
export function onPasswordChangeRequired(listener: () => void): () => void {
  passwordChangeListeners.add(listener);
  return () => {
    passwordChangeListeners.delete(listener);
  };
}

/**
 * Told after any request that changed data succeeds (see api/freshness.ts),
 * whichever screen, dialog or helper sent it. main.tsx marks every cached
 * answer stale on it, so no view is left showing what was true before.
 */
const dataChanged = createChangeSignal();
export const onDataChanged = dataChanged.subscribe;

let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = tokenStore.refresh;
  if (!refreshToken) return false;

  // One refresh at a time: a page with six queries must not burn six
  // single-use refresh tokens and log the user out.
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant': tenantStore.get() },
        body: JSON.stringify({ refreshToken }),
      });
      if (res.status === 401 || res.status === 403) {
        // Refused, not unreachable: these tokens are dead. Offline or a 5xx
        // keeps them, since the next attempt may well succeed.
        tokenStore.clear();
        for (const listener of expiredListeners) listener();
        return false;
      }
      if (!res.ok) return false;
      const data = (await res.json()) as { tokens: AuthTokens; user: SessionUser };
      tokenStore.set(data.tokens);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so concurrent callers share this result.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skips the retry-after-refresh path (used by auth calls themselves). */
  skipAuthRetry?: boolean;
  query?: Record<string, string | number | boolean | undefined>;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipAuthRetry, query, ...init } = options;

  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
    }
  }

  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (body !== undefined && !(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const access = tokenStore.access;
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const company = companyStore.get();
  if (company) headers.set('X-Company', company);
  headers.set('X-Language', languageStore.get());
  // Omitted rather than sent empty before the workspace is known, so the
  // server applies its own default explicitly instead of on a falsy header.
  const tenant = tenantStore.get();
  if (tenant) headers.set('X-Tenant', tenant);

  const send = () =>
    fetch(url.toString(), {
      ...init,
      headers,
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });

  let response = await send();

  if (response.status === 401 && !skipAuthRetry && (await refreshTokens())) {
    const renewed = tokenStore.access;
    if (renewed) headers.set('Authorization', `Bearer ${renewed}`);
    response = await send();
  }

  // A write that succeeded, even one answered with no body, has changed what
  // some other screen shows.
  if (response.ok && changesData(init.method, path)) dataChanged.notify();

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok) {
    const errorBody: ApiErrorBody = contentType.includes('application/json')
      ? ((await response.json()) as ApiErrorBody)
      : { statusCode: response.status, error: response.statusText, message: await response.text() };
    if (response.status === 403 && errorBody.code === 'auth.password_change_required') {
      for (const listener of passwordChangeListeners) listener();
    }
    throw new ApiError(response.status, errorBody);
  }

  if (contentType.includes('application/json')) return (await response.json()) as T;
  return (await response.blob()) as T;
}

export const api = {
  get: <T,>(path: string, query?: RequestOptions['query']) => request<T>(path, { query }),
  post: <T,>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),

  /** Multipart POST. The browser sets the boundary, so no content type here. */
  upload: <T,>(path: string, file: File, query?: RequestOptions['query'], fields?: Record<string, string>) => {
    const form = new FormData();
    form.append('file', file);
    for (const [key, value] of Object.entries(fields ?? {})) form.append(key, value);
    return request<T>(path, { method: 'POST', body: form, query });
  },

  /** Downloads a generated file and hands the browser a save dialog. */
  async download(path: string, body: unknown, fallbackName: string): Promise<void> {
    const blob = await request<Blob>(path, { method: 'POST', body });
    saveBlob(blob, fallbackName);
  },

  /** Same, for endpoints that serve the file on a GET. */
  async downloadGet(path: string, fallbackName: string, query?: RequestOptions['query']): Promise<void> {
    const blob = await request<Blob>(path, { query });
    saveBlob(blob, fallbackName);
  },
};

/**
 * POSTs and reads the reply as server-sent events, one callback per event.
 *
 * `EventSource` cannot send a body or an Authorization header, so this reads
 * the stream from `fetch` instead. Same headers and the same one-shot refresh
 * on 401 as `request`, so a chat opened just as the access token expires
 * does not fail on its first question.
 */
export async function streamEvents(
  path: string,
  body: unknown,
  onEvent: (event: { type: string } & Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  const send = () => {
    const headers = new Headers({ Accept: 'text/event-stream', 'Content-Type': 'application/json' });
    const access = tokenStore.access;
    if (access) headers.set('Authorization', `Bearer ${access}`);
    const company = companyStore.get();
    if (company) headers.set('X-Company', company);
    headers.set('X-Language', languageStore.get());
    const tenant = tenantStore.get();
    if (tenant) headers.set('X-Tenant', tenant);
    return fetch(url.toString(), { method: 'POST', headers, body: JSON.stringify(body), signal });
  };

  let response = await send();
  if (response.status === 401 && (await refreshTokens())) response = await send();

  if (!response.ok || !response.body) {
    const text = await response.text();
    let parsed: ApiErrorBody;
    try {
      parsed = JSON.parse(text) as ApiErrorBody;
    } catch {
      parsed = { statusCode: response.status, error: response.statusText, message: text };
    }
    throw new ApiError(response.status, parsed);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Events end with a blank line; anything after the last one is still arriving.
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const chunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const data = chunk
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n');
      if (data) {
        try {
          onEvent(JSON.parse(data));
        } catch {
          // A malformed event is dropped rather than ending the whole answer.
        }
      }
      boundary = buffer.indexOf('\n\n');
    }
  }
}

function saveBlob(blob: Blob, fallbackName: string): void {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = fallbackName;
  // Firefox ignores a click on an anchor that is not in the document, and
  // revoking in the same tick can cancel the download before it starts.
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
